import { Layout, Menu, Modal } from 'antd';
import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  RadarChartOutlined,
  BarChartOutlined,
  CameraOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  WalletOutlined,
  SearchOutlined,
  // Only keep the icons that are actually used in sidebarItems or visible menu items
} from '@ant-design/icons';
import DashboardHeader from '../header';
import { SubscriptionExpiryBanner } from '../common/SubscriptionExpiryBanner';
import { DateRangeProvider } from '@/context/DateRangeContext';
import { PageErrorBoundary } from '../common/PageErrorBoundary';

const { Content, Sider } = Layout;

// Cleaned up sidebarItems: removed any reference to ControlOutlined and duplicate keys
const sidebarItems = [
  {
    key: '/dashboard/insight-360',
    label: <Link to="/dashboard/insight-360">Insight 360</Link>,
    icon: <RadarChartOutlined />,
  },
  {
    key: '/dashboard/growth-tracking',
    label: <Link to="/dashboard/growth-tracking">Growth Tracking</Link>,
    icon: <BarChartOutlined />,
  },
  {
    key: '/dashboard/snap-suite',
    label: <Link to="/dashboard/snap-suite">Snapsuite</Link>,
    icon: <CameraOutlined />,
  },
  {
    key: '/dashboard/campaign-central',
    label: <Link to="/dashboard/campaign-central">Campaign Central</Link>,
    icon: <DashboardOutlined />,
  },
  {
    key: '/dashboard/day-parting',
    label: <Link to="/dashboard/day-parting">Dayparting</Link>,
    icon: <ClockCircleOutlined />,
  },
  {
    key: '/dashboard/sellco-ai',
    label: <Link to="/dashboard/sellco-ai">Sellco AI</Link>,
    icon: <RobotOutlined />,
  },
  {
    key: '/dashboard/ads',
    label: 'Ads',
    icon: <AppstoreOutlined />,
    children: [
      {
        key: '/dashboard/ads/overview',
        label: <Link to="/dashboard/ads/overview">Overview</Link>,
        icon: <DashboardOutlined />,
      },
      {
        key: '/dashboard/ads/adsdata',
        label: <Link to="/dashboard/ads/adsdata">Insights</Link>,
        icon: <BarChartOutlined />,
      },
      {
        key: '/dashboard/ads/automation/workflows',
        label: <Link to="/dashboard/ads/automation/workflows">Automation</Link>,
        icon: <RobotOutlined />,
      },
      // {
      //   key: '/dashboard/ads/dayparting',
      //   label: <Link to="/dashboard/ads/dayparting">Dayparting</Link>,
      //   icon: <ClockCircleOutlined />,
      // },
      // {
      //   key: '/dashboard/ads/manage/campaigns',
      //   label: <Link to="/dashboard/ads/campaigns">Campaign Manager</Link>,
      //   icon: <AppstoreOutlined />,
      // },
      {
        key: '/dashboard/ads/targets-and-search-terms',
        label: <Link to="/dashboard/ads/targets-and-search-terms">Targets & Search Terms</Link>,
        icon: <SearchOutlined />,
      },
      // {
      //   key: '/dashboard/ads/dsp',
      //   label: <Link to="/dashboard/ads/dsp">DSP</Link>,
      //   icon: <AppstoreOutlined />,
      // },
    ],
  },
  {
    key: '/dashboard/ads/automation/workflows/budget-dayparting',
    label: (
      <Link to="/dashboard/ads/automation/workflows/budget-dayparting">
        Budget Dayparting
      </Link>
    ),
    icon: <WalletOutlined />,
  },
];

const collapseTriggerStyle: React.CSSProperties = {
  backgroundColor: '#232f3e',
  color: 'rgba(255, 255, 255, 0.65)',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'color 0.3s',
};

export const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  return (
    <DateRangeProvider>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          theme="light"
          width={240}
          style={{
            backgroundColor: '#232f3e',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
          collapsible
          collapsedWidth={60}
          trigger={
            <div
              style={collapseTriggerStyle}
              onMouseEnter={(e) => {
                (e.target as HTMLDivElement).style.color = '#ff9900';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLDivElement).style.color =
                  'rgba(255, 255, 255, 0.65)';
              }}
            >
              {collapsed ? '>' : '<'}
            </div>
          }
        >
          <div
            className={`flex items-center w-full p-2 gap-2 h-16 ${collapsed ? 'justify-center' : 'justify-between'
              }`}
            style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
          >
            <div
              style={{ height: 36 }}
              className="flex items-center justify-center "
            >
              <Link
                to="/dashboard"
                className="flex justify-center items-center"
              >
                <img
                  src="/sellco-logo.png"
                  width={36}
                  height={36}
                  alt="Sellco.ai Logo"
                />
              </Link>
            </div>
          </div>

          <Menu
            selectedKeys={[location.pathname]}
            mode="inline"
            inlineCollapsed={collapsed}
            items={sidebarItems}
            style={{
              backgroundColor: '#232f3e',
              borderRight: 'none',
            }}
            className="border-none shadow-none"
            theme="dark"
          />
        </Sider>

        <Layout>
          <Content
            style={{
              height: '100vh',
              overflow: 'auto',
              backgroundColor: '#f2f2f2',
            }}
          >
            <div
              style={{
                height: '100vh',
                overflow: 'auto',
              }}
            >
              <DashboardHeader />
              <SubscriptionExpiryBanner />
              <Content className="p-4">
                <PageErrorBoundary resetKey={location.pathname}>
                  <Outlet />
                </PageErrorBoundary>
              </Content>
            </div>
          </Content>
        </Layout>
        {/* Modal for Creating Workspace */}
        <Modal
          // title="Create New Workspace"
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
        />
      </Layout>
    </DateRangeProvider>
  );
};

export default DashboardLayout;
